import { POLICY_KIND, POLICY_KIND_TITLES } from './policyKinds';

export type CheckoutProduct = 'car' | 'stay' | 'service';

export type PolicyDocumentRow = {
  kind: string;
  title?: string | null;
  ownedByBrand?: boolean | null;
  current?: { id?: string | null; title?: string | null } | null;
};

const REQUIRED: Record<CheckoutProduct, string[]> = {
  car: ['RENTAL', POLICY_KIND.TERMS, POLICY_KIND.CANCELLATION, POLICY_KIND.PRIVACY],
  stay: [POLICY_KIND.TERMS, POLICY_KIND.CANCELLATION, POLICY_KIND.PRIVACY],
  service: [POLICY_KIND.TERMS, POLICY_KIND.PRIVACY],
};

export function rentalAgreementOf(documents: readonly PolicyDocumentRow[]): {
  kind: string;
  href: string;
  versionId: string | null;
} {
  const renter = documents.find(doc => doc.kind === POLICY_KIND.RENTER_AGREEMENT);
  if (renter?.ownedByBrand && renter.current?.id) {
    return {
      kind: POLICY_KIND.RENTER_AGREEMENT,
      href: '/policies/renter-agreement',
      versionId: renter.current.id,
    };
  }
  const shared = documents.find(doc => doc.kind === POLICY_KIND.CAR_SHARING_AGREEMENT);
  return {
    kind: POLICY_KIND.CAR_SHARING_AGREEMENT,
    href: '/policies/car-sharing-agreement',
    versionId: shared?.current?.id ?? null,
  };
}

/** Terms and privacy ids shown on the sign-up form. Empty until a version is published. */
export function signUpPolicyIds(documents: readonly PolicyDocumentRow[]): string[] {
  return [POLICY_KIND.TERMS, POLICY_KIND.PRIVACY]
    .map(kind => documents.find(doc => doc.kind === kind)?.current?.id)
    .filter((id): id is string => Boolean(id));
}

/** Version ids of the documents this checkout is showing, in required order. */
export function versionIdsFor(
  product: CheckoutProduct,
  documents: readonly PolicyDocumentRow[],
): string[] {
  const rental = rentalAgreementOf(documents);
  const ids: string[] = [];
  for (const kind of REQUIRED[product]) {
    if (kind === 'RENTAL') {
      if (rental.versionId) ids.push(rental.versionId);
      continue;
    }
    const id = documents.find(doc => doc.kind === kind)?.current?.id;
    if (id) ids.push(id);
  }
  return ids;
}

function graphQLError(error: unknown): { code: string | null; message: string; details: any } {
  const first = (error as { graphQLErrors?: Array<{ message?: string; extensions?: any }> } | null)
    ?.graphQLErrors?.[0];
  return {
    code: typeof first?.extensions?.code === 'string' ? first.extensions.code : null,
    message: first?.message?.trim() || '',
    details: first?.extensions?.details ?? null,
  };
}

export function isPolicyVersionOutdated(error: unknown): boolean {
  return graphQLError(error).code === 'POLICY_VERSION_OUTDATED';
}

export function isAcknowledgmentRequired(error: unknown): boolean {
  return graphQLError(error).code === 'POLICY_ACKNOWLEDGMENT_REQUIRED';
}

/** Names the document that changed, from `details.changed` or the server message. */
export function outdatedPolicyMessage(error: unknown): string {
  const refusal = graphQLError(error);
  const changed = Array.isArray(refusal.details?.changed) ? refusal.details.changed : [];
  const names = changed
    .map((kind: unknown) => (typeof kind === 'string' ? POLICY_KIND_TITLES[kind] || kind : ''))
    .filter(Boolean);
  if (names.length === 1) {
    return `The ${names[0]} changed. Please review it again.`;
  }
  if (names.length > 1) {
    return `The ${names.join(' and ')} changed. Please review them again.`;
  }
  return (
    refusal.message || 'A document changed while you were checking out. Please review it again.'
  );
}
